import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { hasProjectPermission } from "@/app/capabilities/api/check-permission";
import { projectCapabilitiesQueryOptions } from "@/app/capabilities/api/get-capabilities";
import { PROJECT } from "@/app/capabilities/permissions";
import { projectInvitationsQueryOptions } from "@/app/invitations/api/get-project-invitations";
import { projectMembersQueryOptions } from "@/app/members/api/get-project-members";
import { MembersPageSkeleton } from "@/app/members/components/members-page-skeleton";
import { MembersPage } from "@/app/members/page";
import { RouteErrorBoundary } from "@/components/custom/router/route-error-boundary";
import { requireOneOfProjectPermission } from "@/lib/route-guards";

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/project/members"
)({
  beforeLoad: async (ctx) => {
    // The page renders a members card and an invitations card, each gated on its
    // own read token; allow the route when the caller can read either one.
    await requireOneOfProjectPermission(ctx, [
      PROJECT.members.read,
      PROJECT.invitations.read,
    ]);
  },
  loader: async ({ context: { queryClient }, params }) => {
    const { organizationId, projectId } = params;
    // Capabilities are cached by the beforeLoad guard. Prefetch each list only when the
    // caller holds its read token — both endpoints 403 without it.
    const capabilities = await queryClient.ensureQueryData(
      projectCapabilitiesQueryOptions({ organizationId, projectId })
    );
    if (hasProjectPermission(capabilities.authz, PROJECT.members.read)) {
      queryClient.prefetchQuery(
        projectMembersQueryOptions(organizationId, projectId)
      );
    }
    if (hasProjectPermission(capabilities.authz, PROJECT.invitations.read)) {
      queryClient.prefetchQuery(
        projectInvitationsQueryOptions(organizationId, projectId)
      );
    }
    return { crumb: "Members" };
  },
  errorComponent: RouteErrorBoundary,
  component: () => (
    <Suspense fallback={<MembersPageSkeleton />}>
      <MembersPage />
    </Suspense>
  ),
});
